import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

const ReviewAnswers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionId } = location.state || {};
  
  const [userAnswers, setUserAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showExplanation, setShowExplanation] = useState(true);

  const Icons = {
    back: <i className="mdi mdi-arrow-left"></i>,
    correct: <i className="mdi mdi-check"></i>,
    incorrect: <i className="mdi mdi-close"></i>,
    time: <i className="mdi mdi-clock"></i>,
    brain: <i className="mdi mdi-brain"></i>,
    volume: <i className="mdi mdi-volume-high"></i>,
    suggestion: <i className="mdi mdi-alert-circle"></i>,
    thumbUp: <i className="mdi mdi-thumb-up"></i>,
    thumbDown: <i className="mdi mdi-thumb-down"></i>
  };

  const AnswerIcons = {
    correct: <i className="mdi mdi-check-circle" style={{ fontSize: '24px', color: '#28a745', lineHeight: '1' }}></i>,
    incorrect: <i className="mdi mdi-close-circle" style={{ fontSize: '24px', color: '#dc3545', lineHeight: '1' }}></i>
  };

  const StatsIcons = {
    correct: <i className="mdi mdi-check-circle"></i>,
    incorrect: <i className="mdi mdi-close-circle"></i>,
    time: <i className="mdi mdi-clock"></i>,
    confidence: <i className="mdi mdi-brain"></i>
  };

  useEffect(() => {
    if (sessionId) {
      fetchUserAnswers();
    } else {
      setError('No session ID provided for review');
      setLoading(false);
    }
  }, [sessionId]);

  const fetchUserAnswers = async () => {
    setLoading(true);
    setError(null);
    try {
      const answers = await apiService.getUserAnswers(sessionId);
      console.log('User answers received:', answers);
      setUserAnswers(Array.isArray(answers) ? answers : []);
    } catch (error) {
      console.error('Error fetching user answers:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleNext = () => {
    if (currentQuestionIndex < userAnswers.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const getCorrectAnswerIndex = (correctAnswer, options) => {
    if (typeof correctAnswer === 'number') {
      return Math.max(0, Math.min(correctAnswer, options.length - 1));
    }
    
    if (typeof correctAnswer === 'string') {
      const letter = correctAnswer.toLowerCase().trim();
      
      const letterMap = {
        'a': 0,
        'b': 1, 
        'c': 2,
        'd': 3
      };
      
      if (letter in letterMap) {
        return letterMap[letter];
      }
      
      const matchingIndex = options.findIndex(option => 
        option.toLowerCase().includes(correctAnswer.toLowerCase()) ||
        correctAnswer.toLowerCase().includes(option.toLowerCase())
      );
      
      if (matchingIndex !== -1) {
        return matchingIndex;
      }
      
      return 0; 
    }
    
    return 0; 
  };

  const getLetterFromIndex = (index) => {
    const letterMap = {
      0: 'a',
      1: 'b',
      2: 'c', 
      3: 'd'
    };
    return letterMap[index] || 'a';
  };

  const getAnswerOptionStyles = (index, userAnswer) => {
    const baseCardStyle = {
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: '#e0e0e0',
      opacity: 1,
      position: 'relative',
      transition: 'all 0.2s ease'
    };
    
    const baseRadioStyle = {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: '#666666',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      flexShrink: 0,
      transition: 'all 0.2s ease'
    };

    const correctAnswerIndex = getCorrectAnswerIndex(
      userAnswer.correct_answer, 
      userAnswer.options || []
    );
    const userAnswerIndex = getCorrectAnswerIndex(
      userAnswer.user_answer, 
      userAnswer.options || []
    );

    if (index === correctAnswerIndex) {
      return {
        cardStyle: {
          ...baseCardStyle,
          backgroundColor: 'rgba(40, 167, 69, 0.05)',
          borderColor: '#28a745'
        },
        radioStyle: {
          ...baseRadioStyle,
          borderColor: '#28a745',
          backgroundColor: '#28a745'
        }
      };
    } else if (index === userAnswerIndex && !userAnswer.is_correct) {
      return {
        cardStyle: {
          ...baseCardStyle,
          backgroundColor: 'rgba(220, 53, 69, 0.05)',
          borderColor: '#dc3545'
        },
        radioStyle: {
          ...baseRadioStyle,
          borderColor: '#dc3545',
          backgroundColor: '#dc3545'
        }
      };
    }
    
    return {
      cardStyle: baseCardStyle,
      radioStyle: baseRadioStyle
    };
  };

  const formatSeconds = (seconds) => {
    if (!seconds) return '0 seconds';
    if (seconds < 60) {
      return `${seconds} seconds`;
    } else {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}m ${secs}s`;
    }
  };

  const getMasteryColor = (mastery) => {
    switch (mastery) {
      case 'mastered': return '#28a745';
      case 'almost there': return '#ffc107';
      case 'needs work': return '#dc3545';
      default: return '#666666';
    }
  };

  const parseAIExplanation = (explanation) => {
    try {
      if (typeof explanation === 'string') {
        return JSON.parse(explanation);
      }
      return explanation || { explanation: 'No explanation available', key_take_aways: 'No key takeaways available' };
    } catch (error) {
      return {
        explanation: explanation || 'No explanation available',
        key_take_aways: 'No key takeaways available'
      };
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        <link href="https://cdn.materialdesignicons.com/7.4.47/css/materialdesignicons.min.css" rel="stylesheet" />
        
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <div style={styles.loadingText}>Loading review answers...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        <link href="https://cdn.materialdesignicons.com/7.4.47/css/materialdesignicons.min.css" rel="stylesheet" />
        
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>
            <i className="mdi mdi-alert-circle" style={{fontSize: '48px', color: '#dc3545'}}></i>
          </div>
          <h3 style={styles.errorTitle}>Unable to Load Answers</h3>
          <p style={styles.errorText}>{error}</p>
          <button style={styles.retryButton} onClick={fetchUserAnswers}>
            <i className="mdi mdi-reload" style={{marginRight: '8px'}}></i>
            Try Again
          </button>
          <button style={styles.backButton} onClick={handleBack}>
            <i className="mdi mdi-arrow-left" style={{marginRight: '8px'}}></i>
            Back to Results
          </button>
        </div>
      </div>
    );
  }

  if (!userAnswers || userAnswers.length === 0) {
    return (
      <div style={styles.container}>
        <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        <link href="https://cdn.materialdesignicons.com/7.4.47/css/materialdesignicons.min.css" rel="stylesheet" />
        
        <div style={styles.emptyContainer}>
          <div style={styles.emptyIcon}>
            <i className="mdi mdi-inbox" style={{fontSize: '48px', color: '#666666'}}></i>
          </div>
          <h3 style={styles.emptyTitle}>No Answers Found</h3>
          <p style={styles.emptyText}>No answers were recorded for this session.</p>
          <button style={styles.backButton} onClick={handleBack}>
            <i className="mdi mdi-arrow-left" style={{marginRight: '8px'}}></i>
            Back to Results
          </button>
        </div>
      </div>
    );
  }

  const currentAnswer = userAnswers[currentQuestionIndex];
  const aiExplanation = parseAIExplanation(currentAnswer.ai_explanation);
  const correctAnswerIndex = getCorrectAnswerIndex(
    currentAnswer.correct_answer, 
    currentAnswer.options || []
  );
  const userAnswerIndex = getCorrectAnswerIndex(
    currentAnswer.user_answer, 
    currentAnswer.options || []
  );

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://cdn.materialdesignicons.com/7.4.47/css/materialdesignicons.min.css" rel="stylesheet" />
      
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <button 
            onClick={handleBack}
            style={styles.backButtonHeader}
          >
            <div style={styles.backIcon}>
              {Icons.back}
            </div>
          </button>
          
          <div style={styles.progressInfo}>
            <div style={styles.progressText}>
              Reviewing Answers ({currentQuestionIndex + 1} of {userAnswers.length})
            </div>
            <div style={styles.sessionInfo}>
              Session: {sessionId?.substring(0, 8)}...
            </div>
          </div>

          <div style={styles.navigationControls}>
            <button 
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              style={{
                ...styles.navButton,
                ...(currentQuestionIndex === 0 ? styles.navButtonDisabled : {})
              }}
            >
              <i className="mdi mdi-chevron-left"></i>
              Previous
            </button>
            <button 
              onClick={handleNext}
              disabled={currentQuestionIndex === userAnswers.length - 1}
              style={{
                ...styles.navButton,
                ...(currentQuestionIndex === userAnswers.length - 1 ? styles.navButtonDisabled : {})
              }}
            >
              Next
              <i className="mdi mdi-chevron-right"></i>
            </button>
          </div>
        </div>
      </header>

      <div style={styles.content}>
        <div style={styles.questionContainer}>
          <div style={styles.questionSection}>
            <div style={styles.questionHeader}>
              <h2 style={styles.questionNumber}>Question {currentQuestionIndex + 1}</h2>
              <div style={styles.questionMeta}>
                <span style={styles.topicBadge}>{currentAnswer.topic}</span>
                <span style={styles.difficultyBadge}>{currentAnswer.difficulty}</span>
              </div>
            </div>
            <div style={styles.questionText}>
              {currentAnswer.question_text}
            </div>
          </div>

          <div style={styles.answersSection}>
            <div style={styles.answersTitle}>Answer Options</div>
            <div style={styles.answersList}>
              {(currentAnswer.options || []).map((option, index) => {
                const { cardStyle, radioStyle } = getAnswerOptionStyles(index, currentAnswer);
                
                return (
                  <div key={index} style={cardStyle}>
                    <div style={styles.answerOption}>
                      <div style={styles.optionLeft}>
                        <div style={radioStyle}>
                          {(index === correctAnswerIndex || (index === userAnswerIndex && currentAnswer.is_correct)) && (
                            <div style={styles.radioInner}></div>
                          )}
                        </div>
                        <div style={styles.optionText}>{option}</div>
                      </div>
                      <div style={styles.optionStatus}>
                        {index === correctAnswerIndex && (
                          <div style={styles.correctIcon}>
                            {AnswerIcons.correct}
                          </div>
                        )}
                        {index === userAnswerIndex && !currentAnswer.is_correct && (
                          <div style={styles.incorrectIcon}>
                            {AnswerIcons.incorrect}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={styles.answerSummary}>
            <div style={styles.summaryGrid}>
              <div style={styles.summaryItem}>
                <div style={styles.summaryIcon}>
                  {currentAnswer.is_correct ? Icons.thumbUp : Icons.thumbDown}
                </div>
                <div style={styles.summaryContent}>
                  <div style={styles.summaryValue}>
                    {currentAnswer.is_correct ? 'Correct' : 'Incorrect'}
                  </div>
                  <div style={styles.summaryLabel}>Your Answer</div>
                </div>
              </div>
              
              <div style={styles.summaryItem}>
                <div style={styles.summaryIcon}>
                  {StatsIcons.confidence}
                </div>
                <div style={styles.summaryContent}>
                  <div style={styles.summaryValue}>
                    {currentAnswer.confidence_level}
                  </div>
                  <div style={styles.summaryLabel}>Confidence</div>
                </div>
              </div>
              
              <div style={styles.summaryItem}>
                <div style={styles.summaryIcon}>
                  {StatsIcons.time}
                </div>
                <div style={styles.summaryContent}>
                  <div style={styles.summaryValue}>
                    {formatSeconds(currentAnswer.time_spent)}
                  </div>
                  <div style={styles.summaryLabel}>Time Spent</div>
                </div>
              </div>
              
              <div style={styles.summaryItem}>
                <div style={styles.summaryIcon}>
                  <i className="mdi mdi-star" style={{color: getMasteryColor(currentAnswer.mastery)}}></i>
                </div>
                <div style={styles.summaryContent}>
                  <div style={styles.summaryValue}>
                    {currentAnswer.mastery}
                  </div>
                  <div style={styles.summaryLabel}>Mastery Level</div>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.explanationSection}>
            <div style={styles.explanationHeader}>
              <h3 style={styles.explanationTitle}>
                <i className="mdi mdi-lightbulb" style={{marginRight: '8px', color: '#ffc107'}}></i>
                Explanation
              </h3>
            </div>
            
            <div style={styles.explanationContent}>
              {aiExplanation.explanation || aiExplanation.explanantion || 'No explanation available.'}
            </div>

            {aiExplanation.key_take_aways && (
              <div style={styles.takeawaysSection}>
                <h4 style={styles.takeawaysTitle}>
                  <i className="mdi mdi-key" style={{marginRight: '8px', color: '#2c5aa0'}}></i>
                  Key Takeaways
                </h4>
                <div style={styles.takeawaysContent}>
                  {aiExplanation.key_take_aways}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    fontFamily: '"Open Sans", sans-serif'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    gap: '20px'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #2c5aa0',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loadingText: {
    fontSize: '18px',
    color: '#2c5aa0',
    fontWeight: '500'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    padding: '20px',
    textAlign: 'center',
    gap: '20px'
  },
  errorIcon: {
    marginBottom: '10px'
  },
  errorTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#333333',
    margin: '0 0 10px 0'
  },
  errorText: {
    fontSize: '16px',
    color: '#666666',
    margin: '0 0 20px 0',
    maxWidth: '500px'
  },
  retryButton: {
    backgroundColor: '#2c5aa0',
    border: 'none',
    borderRadius: '6px',
    padding: '12px 24px',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px'
  },
  backButton: {
    backgroundColor: 'transparent',
    border: '1px solid #2c5aa0',
    borderRadius: '6px',
    padding: '12px 24px',
    color: '#2c5aa0',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center'
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    padding: '20px',
    textAlign: 'center',
    gap: '20px'
  },
  emptyIcon: {
    marginBottom: '10px'
  },
  emptyTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#333333',
    margin: '0 0 10px 0'
  },
  emptyText: {
    fontSize: '16px',
    color: '#666666',
    margin: '0 0 20px 0'
  },
  header: {
    backgroundColor: '#2c5aa0',
    height: '70px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center'
  },
  headerContent: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  backButtonHeader: {
    width: '40px',
    height: '40px',
    borderRadius: '20px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  backIcon: {
    color: 'white',
    width: '24px',
    height: '24px'
  },
  progressInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: 'white'
  },
  progressText: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '4px'
  },
  sessionInfo: {
    fontSize: '12px',
    opacity: '0.8'
  },
  navigationControls: {
    display: 'flex',
    gap: '10px'
  },
  navButton: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '6px',
    padding: '8px 16px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background-color 0.2s'
  },
  navButtonDisabled: {
    opacity: '0.5',
    cursor: 'not-allowed'
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '30px 20px'
  },
  questionContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e0e0e0',
    overflow: 'hidden'
  },
  questionSection: {
    padding: '30px'
  },
  questionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px'
  },
  questionNumber: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#2c5aa0',
    margin: '0'
  },
  questionMeta: {
    display: 'flex',
    gap: '10px'
  },
  topicBadge: {
    backgroundColor: '#e3f2fd',
    color: '#2c5aa0',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500'
  },
  difficultyBadge: {
    backgroundColor: '#f0f0f0',
    color: '#666666',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  questionText: {
    fontSize: '18px',
    lineHeight: '1.6',
    color: '#333333',
    fontWeight: '500'
  },
  answersSection: {
    padding: '0 30px 30px'
  },
  answersTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#666666',
    marginBottom: '15px'
  },
  answersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  answerOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px'
  },
  optionLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    flex: 1
  },
  radioInner: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'white'
  },
  optionText: {
    fontSize: '16px',
    lineHeight: '1.4',
    color: '#333333',
    flex: 1
  },
  optionStatus: {
    marginLeft: '20px'
  },
  correctIcon: {
    color: '#28a745'
  },
  incorrectIcon: {
    color: '#dc3545'
  },
  answerSummary: {
    backgroundColor: '#f8f9fa',
    padding: '20px 30px',
    borderTop: '1px solid #e0e0e0',
    borderBottom: '1px solid #e0e0e0'
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '20px'
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  summaryIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '20px',
    backgroundColor: '#e3f2fd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#2c5aa0',
    fontSize: '20px'
  },
  summaryContent: {
    display: 'flex',
    flexDirection: 'column'
  },
  summaryValue: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333333',
    textTransform: 'capitalize'
  },
  summaryLabel: {
    fontSize: '12px',
    color: '#666666',
    fontWeight: '500'
  },
  explanationSection: {
    padding: '30px'
  },
  explanationHeader: {
    marginBottom: '20px'
  },
  explanationTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2c5aa0',
    margin: '0',
    display: 'flex',
    alignItems: 'center'
  },
  explanationContent: {
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#333333',
    marginBottom: '20px',
    backgroundColor: '#f8f9fa',
    padding: '20px',
    borderRadius: '8px',
    borderLeft: '4px solid #ffc107'
  },
  takeawaysSection: {
    marginTop: '20px'
  },
  takeawaysTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#2c5aa0',
    margin: '0 0 12px 0',
    display: 'flex',
    alignItems: 'center'
  },
  takeawaysContent: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#333333',
    backgroundColor: '#e3f2fd',
    padding: '16px',
    borderRadius: '8px',
    borderLeft: '4px solid #2c5aa0'
  }
};

export default ReviewAnswers;